import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isSameDay } from 'date-fns';
import { isEqual } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid, Tooltip } from '@mui/material';

import asyncService from '../../../datahub/asyncService';
import getStepDataV2 from '../../components/RemainingActionsCounterCard/getStepDataV2';
import RemainingActionsCounterCard from '../../components/RemainingActionsCounterCard/RemainingActionsCounterCard';
import TimekeepingOverviewCard from '../../components/TimekeepingOverviewCard/TimekeepingOverviewCard';
import selectUser from '../../store/selectors/appSelector';
import {
  selectIsDisputedTimecardUpdating,
  selectIsReloadTimecards,
  selectIsTimecardsLoading,
  selectIsTimekeepingOverviewDataLoading,
  selectTimecards,
  selectTimekeepingOverviewData,
  selectTotalTimecardsCount,
} from '../../store/selectors/timekeepingSelector';
import { PRIMARY_FONT } from '../../theme/fontConstants';
import { showSnackbar } from '../app';

import BatchProcessModalByFilterV2 from './components/BatchProcessModal/BatchProcessModalByFilterV2';
import BatchProcessModalById from './components/BatchProcessModal/BatchProcessModalById';
import ExportView from './components/Exports/ExportViewWrapper';
import SagePayrollButton from './components/SagePayroll';
import TimekeepingBulkButtons from './components/TimekeepingBulkButtons';
import TimekeepingDayWeekFilter from './components/TimekeepingDayWeekFilter';
import TimekeepingGlobalFilters from './components/TimekeepingGlobalFilters';
import TimekeepingStatusTagsFilters from './components/TimekeepingStatusTagsFilters';
import WorkerTimecardSet from './components/WorkerTimecardSet';
import {
  BULK_ACTION_TYPES,
  EXPORT_TYPES,
  TIMEKEEPING_STATUSES,
  TIMEKEEPING_TAGS,
} from './timekeepingConstants';
import {
  extractPlacementIdsFromUserMap,
  getEndOfDayInEpoch,
  getGlobalFilterParams,
  getStartOfDayInEpoch,
  getTotalHourCountFromSelectedTimecards,
  GLOBAL_FILTER_PARAM_KEYS,
  parseOverviewUrlFilters,
  parseSelectedUrlFilters,
} from './timekeepingHelpers';
import { getTimecards, getTimekeepingOverviewDataV2 } from './timekeepingReducer';

const exportButtonStyle = {
  height: '36px',
  fontFamily: PRIMARY_FONT[500],
  textTransform: 'none',
  minWidth: '160px',
  '& .MuiLoadingButton-loadingIndicator': {
    marginLeft: '23px',
  },
};

const INITIAL_FILTERS = {
  page: 0,
  pageSize: 5,
};

const TimekeepingV2 = () => {
  const statusOptions = [
    {
      value: TIMEKEEPING_STATUSES.PENDING,
      name: LanguageConverterV2('timekeeping.status.pending'),
    },
    {
      value: TIMEKEEPING_STATUSES.RESOLVED,
      name: LanguageConverterV2('timekeeping.status.resolved'),
    },
    {
      value: TIMEKEEPING_STATUSES.DISPUTED,
      name: LanguageConverterV2('timekeeping.status.disputed'),
    },
    {
      value: TIMEKEEPING_STATUSES.APPROVED,
      name: LanguageConverterV2('timekeeping.status.approved'),
    },
    {
      value: TIMEKEEPING_STATUSES.FINALIZED,
      name: LanguageConverterV2('timekeeping.status.finalized'),
    },
  ];

  const tagOptions = [
    {
      value: TIMEKEEPING_TAGS.DISPUTED,
      name: LanguageConverterV2('timekeeping.daily.tags.disputed'),
    },
    {
      value: TIMEKEEPING_TAGS.MISSING_PUNCH,
      name: LanguageConverterV2('timekeeping.daily.tags.missingPunches'),
    },
    {
      value: TIMEKEEPING_TAGS.LOOKS_GOOD,
      name: LanguageConverterV2('timekeeping.daily.tags.looksGood'),
    },
    {
      value: TIMEKEEPING_TAGS.SUSPICIOUS,
      name: LanguageConverterV2('timekeeping.daily.tags.suspicious'),
    },
  ];

  const formatEpochAsHours = (epoch) => {
    if (epoch && typeof epoch === 'number') {
      return (epoch / 60 / 60 / 1000).toFixed(2);
    }
    return null;
  };

  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const previousFiltersRef = useRef();

  const [isBulkActionModalOpen, setIsBulkActionModalOpen] = useState(undefined);
  const [isBulkIdModalOpen, setIsBulkIdModalOpen] = useState(undefined);
  const [activeExport, setActiveExport] = useState(null);
  const [isSendingBillingEmail, setIsSendingBillingEmail] = useState(false);

  const candidateTimecards = useSelector(selectTimecards);
  const total = useSelector(selectTotalTimecardsCount);
  const isDisputedTimecardUpdating = useSelector(selectIsDisputedTimecardUpdating);
  const isTimecardsLoading = useSelector(selectIsTimecardsLoading);
  const isReloadTimecards = useSelector(selectIsReloadTimecards);
  const timekeepingOverviewData = useSelector(selectTimekeepingOverviewData);
  const isTimekeepingOverviewDataLoading = useSelector(selectIsTimekeepingOverviewDataLoading);

  const [candidateCheckedMap, setCandidateCheckedMap] = useState({});
  const user = useSelector(selectUser);

  const isUserAdminOrRecruiter = user?.role === UserRole.ADMIN || user?.role === UserRole.RECRUITER;

  const isEmployer = user?.role === UserRole.EMPLOYER;

  const overviewTimecardCountReadyToApproveOrResolve =
    timekeepingOverviewData?.timecardCounts?.readyToApproveOrResolve;
  const overviewTimecardCountReadyToFinalize =
    timekeepingOverviewData?.timecardCounts?.readyToFinalize;

  const overviewTimecardApproveOrResolveHoursTotal = formatEpochAsHours(
    isEmployer
      ? timekeepingOverviewData?.workedTimeEpoch?.corporation?.readyToApproveOrResolveEpoch
      : timekeepingOverviewData?.workedTimeEpoch?.readyToApproveOrResolveEpoch
  );

  const overviewTimecardFinalizeHoursTotal = formatEpochAsHours(
    timekeepingOverviewData?.workedTimeEpoch?.readyToFinalizeEpoch
  );

  const overviewWorkedTimeEpoch = isEmployer
    ? timekeepingOverviewData?.workedTimeEpoch?.corporation
    : timekeepingOverviewData?.workedTimeEpoch;
  const overviewRegularHours = overviewWorkedTimeEpoch?.regular;
  const overviewOverTimeHours = overviewWorkedTimeEpoch?.overtime;
  const overviewTotalHours = overviewWorkedTimeEpoch?.total;

  const selectedStatuses = (searchParams.get('status') || '').split(',').filter(Boolean);

  const initialStatus =
    selectedStatuses?.length > 0
      ? statusOptions.filter((st) => selectedStatuses.includes(st.value))
      : [];
  const selectedTags = (searchParams.get('tags') || '').split(',').filter(Boolean);
  const initialTags =
    selectedTags?.length > 0 ? tagOptions.filter((tag) => selectedTags.includes(tag.value)) : [];
  const selectedEmployeeId = searchParams.get('employee');
  const selectedCorporationId = isEmployer
    ? user?.employer?.corporation
    : searchParams.get('corporation');
  const selectedAgencyId = searchParams.get('agency');
  const selectedDepartmentId = searchParams.get('department');
  const selectedLocationId = searchParams.get('location');
  const currentPage = parseInt(searchParams.get('page'), 10) || INITIAL_FILTERS.page;
  const currentPageSize = parseInt(searchParams.get('pageSize'), 10) || INITIAL_FILTERS.pageSize;
  const [filterAndSortData, setFilterAndSortData] = useState(() => {
    const initFilters = parseSelectedUrlFilters(searchParams);
    return {
      page: currentPage,
      pageSize: currentPageSize,
      filters: initFilters,
    };
  });

  const currentStart = searchParams.get('startDate');
  const startEpoch = parseInt(currentStart, 10);
  const currentEnd = searchParams.get('endDate');
  const endEpoch = parseInt(currentEnd, 10);
  const isDaily = isSameDay(startEpoch, endEpoch);

  const handleOnWeeklyDailyFilterChange = useCallback(
    (val) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      newParams.startDate = val.startDate;
      newParams.endDate = val?.endDate;
      setCandidateCheckedMap({});
      setSearchParams(newParams);
    },
    [setSearchParams, searchParams]
  );

  const handleStatusChange = useCallback(
    (newStatus) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      if (newStatus.length === 0) {
        delete newParams.status;
      } else {
        newParams.status = newStatus.join(',');
      }

      if (newParams.status !== searchParams.get('status')) {
        setCandidateCheckedMap({});
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const handleTagsChange = useCallback(
    (newTags) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      if (newTags.length === 0) {
        delete newParams.tags;
      } else {
        newParams.tags = newTags.join(',');
      }

      if (newParams.tags !== searchParams.get('tags')) {
        setCandidateCheckedMap({});
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const handleEmployeeSearchChange = useCallback(
    (employeeId) => {
      if (employeeId !== searchParams.get('employee')) {
        const newParams = { ...Object.fromEntries(searchParams) };
        if (employeeId) {
          newParams.employee = employeeId;
        } else {
          delete newParams.employee;
        }
        setCandidateCheckedMap({});
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const handleCorporationChange = useCallback(
    (corporationId) => {
      if (corporationId !== searchParams.get('corporation')) {
        const newParams = { ...Object.fromEntries(searchParams) };
        if (corporationId) {
          newParams.corporation = corporationId;
        } else {
          delete newParams.corporation;
        }
        setCandidateCheckedMap({});
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const handleDepartmentChange = useCallback(
    (department) => {
      if (department !== searchParams.get('department')) {
        const newParams = { ...Object.fromEntries(searchParams) };
        if (department) {
          newParams.department = department;
        } else {
          delete newParams.department;
        }
        setCandidateCheckedMap({});
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const handleAgencyChange = useCallback(
    (agencyId) => {
      if (agencyId !== searchParams.get('agency')) {
        const newParams = { ...Object.fromEntries(searchParams) };
        if (agencyId) {
          newParams.agency = agencyId;
        } else {
          delete newParams.agency;
        }
        setCandidateCheckedMap({});
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const handleLocationChange = useCallback(
    (location) => {
      if (location !== searchParams.get('location')) {
        const newParams = { ...Object.fromEntries(searchParams) };
        if (location) {
          newParams.location = location;
        } else {
          delete newParams.location;
        }
        setCandidateCheckedMap({});
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );
  const handlePositionSearchChange = useCallback(
    (newPositionFilter) => {
      const positionSearchTerm = newPositionFilter?.[0]?.value;

      const newParams = { ...Object.fromEntries(searchParams) };
      if (!positionSearchTerm) {
        delete newParams.position;
      } else {
        newParams.position = positionSearchTerm;
      }
      setCandidateCheckedMap({});
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleExportBilling = useCallback(() => {
    setIsSendingBillingEmail(true);
    asyncService({
      httpMethod: 'POST',
      route: 'billing-data',
      data: {
        page: 1,
        limit: 1,
        filters: [
          { field: 'start', value: startEpoch, operation: 'onOrAfter' },
          { field: 'start', value: endEpoch, operation: 'before' },
        ],
      },
      onSuccess: () => {
        setIsSendingBillingEmail(false);
        dispatch(showSnackbar({ message: 'billing.email.sent' }));
      },
      onError: () => {
        setIsSendingBillingEmail(false);
        dispatch(showSnackbar({ message: 'billing.email.error' }));
      },
      dispatch,
      token: user?.token,
      refreshToken: user?.refreshToken,
    });
  }, [dispatch, user?.token, user?.refreshToken, startEpoch, endEpoch]);

  const clearGlobalFilters = useCallback(() => {
    const newParams = { ...Object.fromEntries(searchParams) };
    GLOBAL_FILTER_PARAM_KEYS.forEach((paramKey) => {
      delete newParams[paramKey];
    });
    setCandidateCheckedMap({});
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const startDateFilter = filterAndSortData.filters?.filter((f) => f.field === 'start');
    if (!isTimecardsLoading && startDateFilter?.length === 2) {
      const payload = {
        filters: filterAndSortData.filters,
        limit: filterAndSortData.pageSize,
        page: filterAndSortData.page,
      };
      if (!isEqual(previousFiltersRef.current, payload) || isReloadTimecards) {
        previousFiltersRef.current = payload;
        dispatch(getTimecards(payload));
      }
    }
  }, [dispatch, isTimecardsLoading, filterAndSortData, isReloadTimecards]);

  useEffect(() => {
    const searchParamsStartDate = searchParams.get('startDate');
    const searchParamsEndDate = searchParams.get('endDate');
    if (searchParamsStartDate && searchParamsEndDate) {
      dispatch(
        getTimekeepingOverviewDataV2({
          filters: parseOverviewUrlFilters(searchParams),
        })
      );
    }
  }, [dispatch, searchParams]);

  useEffect(() => {
    if (!searchParams.get('startDate') || !searchParams.get('endDate')) {
      setCandidateCheckedMap({});
      setSearchParams({
        startDate: getStartOfDayInEpoch(Date.now()),
        endDate: getEndOfDayInEpoch(Date.now()),
      });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setFilterAndSortData((prev) => {
      const filters = parseSelectedUrlFilters(searchParams);

      if (prev.page !== currentPage || prev.pageSize !== currentPageSize) {
        return { ...prev, filters, page: currentPage, pageSize: currentPageSize };
      }

      if (isEqual(prev.filters, filters)) {
        return prev;
      }

      if (filters.length > 0) {
        setCandidateCheckedMap({});
        setSearchParams({
          ...Object.fromEntries(searchParams),
          page: INITIAL_FILTERS.page,
          pageSize: currentPageSize,
        });
        return { ...prev, filters, page: INITIAL_FILTERS.page, pageSize: currentPageSize };
      }
      return { ...INITIAL_FILTERS };
    });
  }, [currentPage, currentPageSize, searchParams, setSearchParams]);

  const handleOnCardClick = useCallback(
    ({ filterVal, actionType }) => {
      if (actionType === BULK_ACTION_TYPES.RESOLVE) {
        setIsBulkActionModalOpen(BULK_ACTION_TYPES.RESOLVE);
      } else if (actionType === BULK_ACTION_TYPES.APPROVE) {
        setIsBulkActionModalOpen(BULK_ACTION_TYPES.APPROVE);
      } else if (actionType === BULK_ACTION_TYPES.FINALIZE) {
        setIsBulkActionModalOpen(BULK_ACTION_TYPES.FINALIZE);
      }

      setCandidateCheckedMap({});
      setSearchParams(filterVal);
    },
    [setIsBulkActionModalOpen, setSearchParams]
  );

  const globalFilterParams = getGlobalFilterParams(searchParams);
  const stepData = getStepDataV2({
    timecardCounts: timekeepingOverviewData?.timecardCounts,
    role: user.role,
    startDate: searchParams.get('startDate'),
    endDate: searchParams.get('endDate'),
    handleOnCardClick,
    currentParams: {
      startDate: searchParams.get('startDate'),
      endDate: searchParams.get('endDate'),
      ...(searchParams.get('status') && { status: searchParams.get('status') }),
      ...(searchParams.get('tags') && { tags: searchParams.get('tags') }),
      ...globalFilterParams,
    },
    globalFilterParams,
    workedTimeEpochs: timekeepingOverviewData?.workedTimeEpoch,
  });

  const handlePaginationChange = useCallback(
    (page = INITIAL_FILTERS.page, pageSize = INITIAL_FILTERS.pageSize) => {
      // Clear selected timecards when pagination changes
      setCandidateCheckedMap({});
      setSearchParams({ ...Object.fromEntries(searchParams), page, pageSize });
    },
    [searchParams, setSearchParams]
  );

  const itemsToActOn = extractPlacementIdsFromUserMap(candidateCheckedMap);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '24px' }}>
      {isBulkActionModalOpen && (
        <BatchProcessModalByFilterV2
          // Timecard count is set to the amount that need to be approve/resolved or finalized depending on which modal is being used.
          initialTotalTimecardCount={
            ((isBulkActionModalOpen === BULK_ACTION_TYPES.APPROVE ||
              isBulkActionModalOpen === BULK_ACTION_TYPES.RESOLVE) &&
              overviewTimecardCountReadyToApproveOrResolve) ||
            (isBulkActionModalOpen === BULK_ACTION_TYPES.FINALIZE &&
              overviewTimecardCountReadyToFinalize)
          }
          initialTotalHourCount={
            ((isBulkActionModalOpen === BULK_ACTION_TYPES.APPROVE ||
              isBulkActionModalOpen === BULK_ACTION_TYPES.RESOLVE) &&
              overviewTimecardApproveOrResolveHoursTotal) ||
            (isBulkActionModalOpen === BULK_ACTION_TYPES.FINALIZE &&
              overviewTimecardFinalizeHoursTotal)
          }
          isOpen={isBulkActionModalOpen}
          selectedFilters={parseSelectedUrlFilters(searchParams)}
          onClose={() => {
            setIsBulkActionModalOpen(undefined);
            dispatch(
              getTimekeepingOverviewDataV2({
                filters: parseOverviewUrlFilters(searchParams),
              })
            );

            handlePaginationChange(INITIAL_FILTERS.page, currentPageSize);
            const payload = {
              filters: filterAndSortData.filters,
              limit: currentPageSize,
              page: INITIAL_FILTERS.page,
            };
            dispatch(getTimecards(payload));
          }}
          onGoBack={() => {
            setIsBulkActionModalOpen(undefined);
          }}
        />
      )}

      {isBulkIdModalOpen && (
        <BatchProcessModalById
          initialTotalHourCount={getTotalHourCountFromSelectedTimecards(
            itemsToActOn,
            candidateTimecards
          )}
          isOpen={isBulkIdModalOpen}
          onClose={() => {
            setIsBulkIdModalOpen(undefined);
            dispatch(
              getTimekeepingOverviewDataV2({
                filters: parseOverviewUrlFilters(searchParams),
              })
            );
            handlePaginationChange(INITIAL_FILTERS.page, currentPageSize);

            const payload = {
              filters: filterAndSortData.filters,
              limit: currentPageSize,
              page: INITIAL_FILTERS.page,
            };
            dispatch(getTimecards(payload));
            setCandidateCheckedMap({});
          }}
          onGoBack={() => {
            setIsBulkIdModalOpen(undefined);
          }}
        />
      )}
      {activeExport && !isDaily && (
        <ExportView
          filters={[
            { field: 'start', value: startEpoch, operation: 'onOrAfter' },
            { field: 'start', value: endEpoch, operation: 'before' },
          ]}
          user={user}
          activeExport={activeExport}
          onClose={() => setActiveExport(null)}
        />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <TimekeepingDayWeekFilter
          startEpoch={startEpoch}
          endEpoch={endEpoch}
          isDaily={isDaily}
          handleOnWeeklyDailyFilterChange={handleOnWeeklyDailyFilterChange}
        />
        <Grid container sx={{ gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
          {!isDaily && (user?.role === UserRole.ADMIN || user?.role === UserRole.RECRUITER) && (
            <Button
              variant="outlined"
              sx={exportButtonStyle}
              disabled={user?.role === UserRole.RECRUITER} // For now, disable payroll export for recruiters
              onClick={() => {
                setActiveExport(EXPORT_TYPES.PAYROLL);
              }}
            >
              {LanguageConverterV2('button.export.payroll')}
            </Button>
          )}
          {!isDaily && user?.role === UserRole.ADMIN && (
            <>
              <Button
                variant="outlined"
                sx={exportButtonStyle}
                onClick={() => {
                  setActiveExport(EXPORT_TYPES.CS_PAYROLL);
                }}
              >
                {LanguageConverterV2('button.export.careerStart.payroll')}
              </Button>
              <SagePayrollButton sx={exportButtonStyle} />
              <Tooltip title={LanguageConverterV2('tooltip.feature.coming.soon')}>
                <span>
                  <LoadingButton
                    loading={isSendingBillingEmail}
                    loadingPosition="end"
                    variant="outlined"
                    sx={exportButtonStyle}
                    onClick={handleExportBilling}
                    disabled
                  >
                    {LanguageConverterV2('button.export.billing')}
                  </LoadingButton>
                </span>
              </Tooltip>
            </>
          )}
        </Grid>
      </Box>
      <TimekeepingGlobalFilters
        isTimecardsLoading={isTimecardsLoading}
        isUserAdminOrRecruiter={isUserAdminOrRecruiter}
        selectedEmployeeId={selectedEmployeeId}
        selectedCorporationId={selectedCorporationId}
        selectedAgencyId={selectedAgencyId}
        selectedDepartmentId={selectedDepartmentId}
        selectedLocationId={selectedLocationId}
        corporationInitialValue={searchParams.get('corporation')}
        positionInitialValue={searchParams.get('position')}
        handleEmployeeSearchChange={handleEmployeeSearchChange}
        handleCorporationChange={handleCorporationChange}
        handleAgencyChange={handleAgencyChange}
        handleDepartmentChange={handleDepartmentChange}
        handleLocationChange={handleLocationChange}
        handlePositionSearchChange={handlePositionSearchChange}
        clearGlobalFilters={clearGlobalFilters}
      />
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TimekeepingOverviewCard
          isLoading={isTimekeepingOverviewDataLoading}
          titleText={
            isDaily
              ? LanguageConverterV2('timekeeping.overviewCard.dailyOverview')
              : LanguageConverterV2('timekeeping.overviewCard.weeklyOverview')
          }
          numOfTimeCards={timekeepingOverviewData?.timecardCounts?.total}
          regularHours={overviewRegularHours}
          overTimeHours={overviewOverTimeHours}
          totalHours={overviewTotalHours}
        />
        {stepData?.map((step) => (
          <RemainingActionsCounterCard
            isCompleted={step?.isCompleted}
            isLoading={isTimekeepingOverviewDataLoading}
            isSelected={step?.isSelected}
            isFiltered={step?.isFiltered}
            onCardClick={step?.onCardClick}
            header={step?.header}
            subHeader={step?.subHeader}
            body={step?.body}
            count={step?.count}
            color={step?.color}
            buttonText={step?.buttonText}
            disableButton={step?.disableButton}
            hideButton={step?.hideButton}
            key={step.header}
          />
        ))}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <TimekeepingStatusTagsFilters
          isTimecardsLoading={isTimecardsLoading}
          statusOptions={statusOptions}
          tagOptions={tagOptions}
          initialStatus={initialStatus}
          initialTags={initialTags}
          handleStatusChange={handleStatusChange}
          handleTagsChange={handleTagsChange}
        />

        <TimekeepingBulkButtons
          setBulkActionData={() => {}}
          userRole={user?.role}
          selectedPlacementIds={itemsToActOn}
          allTimecards={candidateTimecards}
          // TODO: It's probably not necessary that these are 3 separate props here for opening the different modal types.
          openFinalizeModal={() =>
            setIsBulkIdModalOpen({
              modalType: BULK_ACTION_TYPES.FINALIZE,
              placementIds: itemsToActOn,
            })
          }
          openResolveModal={() =>
            setIsBulkIdModalOpen({
              modalType: BULK_ACTION_TYPES.RESOLVE,
              placementIds: itemsToActOn,
            })
          }
          openApproveModal={() =>
            setIsBulkIdModalOpen({
              modalType: BULK_ACTION_TYPES.APPROVE,
              placementIds: itemsToActOn,
            })
          }
        />
      </Box>

      <WorkerTimecardSet
        candidateTimecards={candidateTimecards}
        total={total}
        isLoading={isTimecardsLoading}
        isDisputedTimecardUpdating={isDisputedTimecardUpdating}
        currentPage={filterAndSortData.page}
        pageSize={filterAndSortData.pageSize}
        onPaginationChange={handlePaginationChange}
        candidateCheckedMap={candidateCheckedMap}
        setCandidateCheckedMap={setCandidateCheckedMap}
      />
    </Box>
  );
};

export default TimekeepingV2;
